import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import BarcodeScanner from "@/components/BarcodeScanner";
import { lookupBarcode, UnknownBarcodeDialog } from "@/components/barcode/BarcodeFlow";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Truck, PackageCheck, Loader2, Printer, ClipboardCheck, AlertTriangle, FileText, XCircle, ScanLine,
} from "lucide-react";

const PO_STATUS_LABEL = {
  draft: "Taslak",
  ordered: "Sipariş Verildi",
  partial_received: "Kısmi Geldi",
  received: "Tamamlandı",
  cancelled: "İptal",
};
const PO_STATUS_TONE = {
  draft: "bg-slate-500/15 text-slate-300 border-slate-500/40",
  ordered: "bg-sky-500/15 text-sky-300 border-sky-500/40",
  partial_received: "bg-amber-500/15 text-amber-300 border-amber-500/40",
  received: "bg-emerald-500/15 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-rose-500/15 text-rose-300 border-rose-500/40",
};

function newCrid() {
  if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  return "gr-" + Math.random().toString(36).slice(2) + Date.now().toString(36);
}

export default function GoodsReceipt() {
  const { can } = useAuth();
  const [pos, setPos] = useState([]);
  const [loadingPos, setLoadingPos] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [poDetail, setPoDetail] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [targetWh, setTargetWh] = useState("");
  const [deliveryNo, setDeliveryNo] = useState("");
  const [receiptDate, setReceiptDate] = useState(() => new Date().toISOString().slice(0, 10));
  const [notes, setNotes] = useState("");
  const [rows, setRows] = useState({}); // {po_item_id: {accepted, damaged, rejected, note}}
  const [crid, setCrid] = useState(newCrid);
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState([]);
  const [cancelPoOpen, setCancelPoOpen] = useState(false);
  const [cancelPoReason, setCancelPoReason] = useState("");
  const [cancelPoConfirmPartial, setCancelPoConfirmPartial] = useState(false);
  const [cancellingPo, setCancellingPo] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [unknownCode, setUnknownCode] = useState("");
  const [scanMatches, setScanMatches] = useState(null); // {label, items:[{po, item}]}
  const [hlLine, setHlLine] = useState("");

  // Barkod yalnız satırı bulur/seçer; teslim adedi veya stok değişmez
  const focusLine = async (poId, itemId) => {
    setScanMatches(null);
    if (poId !== selectedId) await openPo(poId);
    setHlLine(itemId);
    setTimeout(() => document.querySelector(`[data-testid="gr-row-${itemId}"]`)?.scrollIntoView({ block: "center" }), 150);
  };
  const onScanned = async (code) => {
    try {
      const r = await lookupBarcode(code);
      if (!r.found) { setUnknownCode(r.code); return; }
      const vid = r.variant?.id || "";
      const items = [];
      pos.forEach((po) => (po.items || []).forEach((it) => {
        const rem = Number(it.ordered_qty || 0) - Number(it.received_qty_total || 0);
        if (it.product_id === r.product.id && (it.variant_id || "") === vid && rem > 0.001) items.push({ po, item: it, rem });
      }));
      const label = `${r.product.name}${r.size ? ` · ${r.size}` : ""}`;
      if (!items.length) { toast.error(`Açık mal kabul satırı yok: ${label}`); return; }
      if (items.length === 1) { await focusLine(items[0].po.id, items[0].item.id); toast.success(`Satır bulundu: ${label}`); return; }
      setScanMatches({ label, items });
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const loadPos = useCallback(async () => {
    setLoadingPos(true);
    try {
      const { data } = await api.get("/purchase-orders");
      const filtered = (data || []).filter((p) =>
        ["ordered", "partial_received"].includes(p.status || "") &&
        (p.items || []).length > 0
      );
      setPos(filtered);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingPos(false); }
  }, []);

  const loadWarehouses = useCallback(async () => {
    try {
      const { data } = await api.get("/warehouses/summary");
      const active = (data || []).filter((w) => w.is_active !== false);
      setWarehouses(active);
      if (active.length === 1 && !targetWh) setTargetWh(active[0].id);
    } catch { /* silent */ }
  }, [targetWh]);

  useEffect(() => { loadPos(); loadWarehouses(); }, [loadPos, loadWarehouses]);

  const openPo = async (id) => {
    setSelectedId(id);
    try {
      const [{ data: recv }, { data: hist }] = await Promise.all([
        api.get(`/purchase-orders/${id}/receivable-lines`),
        api.get(`/goods-receipts?purchase_order_id=${id}`),
      ]);
      setPoDetail(recv);
      setHistory(hist || []);
      if (recv.target_warehouse_id) setTargetWh(recv.target_warehouse_id);
      const seed = {};
      (recv.lines || []).forEach((l) => { seed[l.id] = { accepted: "", damaged: "", rejected: "", note: "" }; });
      setRows(seed);
      setCrid(newCrid());
      setDeliveryNo("");
      setNotes("");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const updateRow = (id, patch) => setRows((r) => ({ ...r, [id]: { ...r[id], ...patch } }));

  const submit = async () => {
    if (!poDetail) return;
    const hasProduct = (poDetail.lines || []).some((l) => l.kind === "product" || l.product_id);
    if (hasProduct && !targetWh) { toast.error("Hedef depo seçin"); return; }
    const items = [];
    let hasAny = false;
    for (const l of (poDetail.lines || [])) {
      const r = rows[l.id] || {};
      const acc = Number(r.accepted || 0);
      const dmg = Number(r.damaged || 0);
      const rej = Number(r.rejected || 0);
      if (acc <= 0 && dmg <= 0 && rej <= 0) continue;
      hasAny = true;
      const total = acc + dmg + rej;
      if (total - Number(l.remaining_qty || 0) > 0.001) {
        toast.error(`${l.product_name || l.material_name}: girilen (${total}) kalan (${l.remaining_qty}) miktarını aşıyor`);
        return;
      }
      items.push({
        po_item_id: l.id,
        accepted_qty: acc, damaged_qty: dmg, rejected_qty: rej,
        note: r.note || "",
      });
    }
    if (!hasAny) { toast.error("En az bir satırda miktar girin"); return; }

    setSaving(true);
    try {
      const { data } = await api.post("/goods-receipts", {
        client_receipt_id: crid,
        purchase_order_id: selectedId,
        target_warehouse_id: targetWh,
        delivery_note_no: deliveryNo,
        receipt_date: receiptDate,
        notes,
        items,
      });
      toast.success(`Mal kabul tamamlandı: ${data.receipt_no} (${data.status === "received" ? "Tam" : "Kısmi"})`);
      loadPos();
      openPo(selectedId); // refresh detail
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setSaving(false); }
  };

  const openPrint = (poId) => {
    window.open(`/satinalma/po/${poId}/yazdir`, "_blank", "noopener,noreferrer");
  };

  const cancelPo = async () => {
    if (!poDetail) return;
    setCancellingPo(true);
    try {
      await api.post(`/purchase-orders/${selectedId}/cancel`, {
        reason: cancelPoReason.trim(),
        confirm_partial: cancelPoConfirmPartial,
      });
      toast.success("Sipariş iptal edildi");
      setCancelPoOpen(false);
      setCancelPoReason("");
      setCancelPoConfirmPartial(false);
      setPoDetail(null);
      loadPos();
    } catch (err) {
      const msg = formatApiError(err);
      // 409 partial → user must tick confirm
      if (msg.toLowerCase().includes("kısmen") || msg.toLowerCase().includes("yine de")) {
        setCancelPoConfirmPartial(true);
        toast.warning(msg);
      } else {
        toast.error(msg);
      }
    } finally { setCancellingPo(false); }
  };

  const filteredPos = useMemo(() => pos, [pos]);

  return (
    <div className="p-6 space-y-4" data-testid="goods-receipt-page">
      <div>
        <h1 className="text-3xl font-semibold flex items-center gap-2">
          <PackageCheck className="w-7 h-7 text-emerald-400" /> Mal Kabul
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Bekleyen ve kısmi teslim edilen satın alma siparişlerini seçip mal kabulü yapın.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[380px_1fr] gap-4">
        {/* Sol: PO listesi */}
        <Card className="p-2 h-fit">
          <div className="flex items-center justify-between px-2 py-1.5">
            <div className="text-sm font-medium">Bekleyen Siparişler</div>
            <div className="flex items-center gap-2">
              {loadingPos && <Loader2 className="w-4 h-4 animate-spin" />}
              <Button size="sm" variant="outline" onClick={() => setScanOpen(true)} data-testid="gr-scan-btn">
                <ScanLine className="w-4 h-4 mr-1" /> Barkod Tara
              </Button>
            </div>
          </div>
          <div className="space-y-1.5 max-h-[70vh] overflow-y-auto">
            {filteredPos.length === 0 && !loadingPos && (
              <div className="text-sm text-muted-foreground p-4 text-center">
                Bekleyen satın alma siparişi yok.
              </div>
            )}
            {filteredPos.map((p) => (
              <button
                key={p.id}
                onClick={() => openPo(p.id)}
                className={`w-full text-left rounded border p-2 hover:border-emerald-500/60 transition ${selectedId === p.id ? "border-emerald-500 bg-emerald-500/5" : "border-border/40"}`}
                data-testid={`po-item-${p.id}`}
              >
                <div className="flex items-center justify-between">
                  <span className="font-medium text-sm">{p.po_no || p.order_no || p.id.slice(0, 8)}</span>
                  <Badge variant="outline" className={PO_STATUS_TONE[p.status || "draft"]}>
                    {PO_STATUS_LABEL[p.status || "draft"]}
                  </Badge>
                </div>
                <div className="text-xs text-muted-foreground mt-0.5">{p.supplier_name || "—"}</div>
                <div className="text-xs text-muted-foreground">{(p.items || []).length} satır · ₺{Number(p.total_price || 0).toLocaleString("tr-TR")}</div>
              </button>
            ))}
          </div>
        </Card>

        {/* Sağ: Detay */}
        <div className="space-y-4">
          {!poDetail && (
            <Card className="p-8 text-center text-muted-foreground">
              Sol panelden bir sipariş seçin.
            </Card>
          )}
          {poDetail && (
            <>
              <Card className="p-3">
                <div className="flex items-start justify-between gap-2 flex-wrap">
                  <div>
                    <div className="text-lg font-semibold">{poDetail.po_no}</div>
                    <div className="text-sm text-muted-foreground">{poDetail.supplier_name}</div>
                  </div>
                  <div className="flex gap-2">
                    <Badge variant="outline" className={PO_STATUS_TONE[poDetail.status]}>
                      {PO_STATUS_LABEL[poDetail.status]}
                    </Badge>
                    <Button size="sm" variant="outline" onClick={() => openPrint(selectedId)} data-testid="po-print-btn">
                      <Printer className="w-4 h-4 mr-1" /> Yazdır
                    </Button>
                    {["draft", "ordered", "partial_received"].includes(poDetail.status) && (
                      <Button disabled={!(can("purchases", "edit"))} title={!(can("purchases", "edit")) ? "Satın alma siparişi iptal yetkiniz yok (purchases.edit)" : undefined}
                        size="sm"
                        variant="outline"
                        className="border-rose-500/50 text-rose-300 hover:bg-rose-500/10"
                        onClick={() => setCancelPoOpen(true)}
                        data-testid="po-cancel-btn"
                      >
                        <XCircle className="w-4 h-4 mr-1" /> Siparişi İptal Et
                      </Button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-3">
                  <div>
                    <Label>Kabul Tarihi</Label>
                    <Input type="date" value={receiptDate} onChange={(e) => setReceiptDate(e.target.value)} data-testid="gr-date" />
                  </div>
                  <div>
                    <Label>İrsaliye No</Label>
                    <Input value={deliveryNo} onChange={(e) => setDeliveryNo(e.target.value)} data-testid="gr-delivery-no" placeholder="İrsaliye numarası" />
                  </div>
                  <div>
                    <Label>Hedef Depo</Label>
                    <Select value={targetWh} onValueChange={setTargetWh}>
                      <SelectTrigger data-testid="gr-warehouse"><SelectValue placeholder="Seçin" /></SelectTrigger>
                      <SelectContent>
                        {warehouses.map((w) => (
                          <SelectItem key={w.id} value={w.id}>{w.code} · {w.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </Card>

              <Card className="overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-16" data-testid="gr-size-header">Beden</TableHead>
                      <TableHead>Ürün / Malzeme</TableHead>
                      <TableHead className="text-right">Sipariş</TableHead>
                      <TableHead className="text-right">Önceki</TableHead>
                      <TableHead className="text-right">Kalan</TableHead>
                      <TableHead className="text-right w-28">Sağlam</TableHead>
                      <TableHead className="text-right w-24">Hasarlı</TableHead>
                      <TableHead className="text-right w-24">Red</TableHead>
                      <TableHead>Not</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(poDetail.lines || []).map((l) => {
                      const r = rows[l.id] || {};
                      const closed = Number(l.remaining_qty) <= 0.001;
                      return (
                        <TableRow key={l.id} className={`${closed ? "opacity-50" : ""} ${hlLine === l.id ? "ring-2 ring-emerald-500 bg-emerald-500/10" : ""}`} data-testid={`gr-row-${l.id}`} data-highlight={hlLine === l.id ? "1" : undefined}>
                          <TableCell className="w-16 font-mono text-xs" data-testid={`gr-size-${l.id}`}>{l.size || "—"}</TableCell>
                          <TableCell>
                            <div className="font-medium text-sm">
                              {l.product_name || l.material_name || l.material_code || "—"}
                            </div>
                            <div className="text-xs text-muted-foreground">
                              {l.brand_name ? <span className="text-emerald-400/80">{l.brand_name} · </span> : null}
                              {l.variant_sku || l.material_code || ""}
                              {l.unit ? ` · ${l.unit}` : ""}
                              {l.kind ? ` · ${l.kind}` : ""}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">{l.ordered_qty}</TableCell>
                          <TableCell className="text-right text-muted-foreground">{l.received_qty_total}</TableCell>
                          <TableCell className="text-right font-medium">{l.remaining_qty}</TableCell>
                          <TableCell>
                            <Input
                              type="number" min="0" step="0.001"
                              className="h-8 text-right"
                              disabled={closed}
                              value={r.accepted || ""}
                              onChange={(e) => updateRow(l.id, { accepted: e.target.value })}
                              data-testid={`gr-accepted-${l.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min="0" step="0.001"
                              className="h-8 text-right"
                              disabled={closed}
                              value={r.damaged || ""}
                              onChange={(e) => updateRow(l.id, { damaged: e.target.value })}
                              data-testid={`gr-damaged-${l.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min="0" step="0.001"
                              className="h-8 text-right"
                              disabled={closed}
                              value={r.rejected || ""}
                              onChange={(e) => updateRow(l.id, { rejected: e.target.value })}
                              data-testid={`gr-rejected-${l.id}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              className="h-8"
                              disabled={closed}
                              value={r.note || ""}
                              onChange={(e) => updateRow(l.id, { note: e.target.value })}
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>

              <Card className="p-3 space-y-2">
                <div>
                  <Label>Genel Not</Label>
                  <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
                </div>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="text-xs text-muted-foreground">
                    <AlertTriangle className="w-3.5 h-3.5 inline mr-1 text-amber-400" />
                    Hasarlı ve red miktarlar stoğa eklenmez, sadece siparişten düşülür.
                  </div>
                  <Button
                    onClick={submit}
                    disabled={(saving) || !(can("warehouse", "edit"))} title={!(can("warehouse", "edit")) ? "Mal kabul yetkiniz yok (warehouse.edit)" : undefined}
                    className="bg-emerald-600 hover:bg-emerald-500"
                    data-testid="gr-submit-btn"
                  >
                    {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                    <ClipboardCheck className="w-4 h-4 mr-1" /> Mal Kabulü Tamamla
                  </Button>
                </div>
              </Card>

              {/* Geçmiş */}
              {history.length > 0 && (
                <Card className="p-3">
                  <div className="text-sm font-medium mb-2 flex items-center gap-2">
                    <FileText className="w-4 h-4" /> Mal Kabul Geçmişi ({history.length})
                  </div>
                  <div className="space-y-1 text-sm">
                    {history.map((h) => (
                      <div key={h.id} className="flex items-center justify-between border-b pb-1 last:border-b-0">
                        <div>
                          <span className="font-medium">{h.receipt_no}</span>
                          <span className="text-muted-foreground text-xs ml-2">
                            {h.delivery_note_no ? `İrs: ${h.delivery_note_no} · ` : ""}
                            {new Date(h.created_at).toLocaleString("tr-TR")}
                          </span>
                        </div>
                        <Badge variant="outline" className={h.status === "received" ? PO_STATUS_TONE.received : PO_STATUS_TONE.partial_received}>
                          {h.status === "received" ? "Tam" : "Kısmi"}
                        </Badge>
                      </div>
                    ))}
                  </div>
                </Card>
              )}
            </>
          )}
        </div>
      </div>

      {/* PO Cancel dialog */}
      <Dialog open={cancelPoOpen} onOpenChange={(v) => { if (!v) { setCancelPoOpen(false); setCancelPoConfirmPartial(false); } }}>
        <DialogContent className="max-w-md" data-testid="po-cancel-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <XCircle className="w-5 h-5 text-rose-400" /> Satın Alma Siparişini İptal Et
            </DialogTitle>
            <DialogDescription>
              <b>{poDetail?.po_no}</b> — {poDetail?.supplier_name} iptal edilecek.
              Alınmış miktarlar stoğunuzda KALIR. Bu işlem stok hareketi oluşturmaz.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {poDetail?.status === "partial_received" && (
              <div className="p-2 rounded border border-amber-500/40 bg-amber-500/10 text-xs text-amber-300 flex gap-2">
                <AlertTriangle className="w-4 h-4 flex-none mt-0.5" />
                <div>Bu sipariş kısmen teslim alınmış. İptal etmek için aşağıdaki onayı işaretleyin.</div>
              </div>
            )}
            {(poDetail?.status === "partial_received" || cancelPoConfirmPartial) && (
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={cancelPoConfirmPartial}
                  onChange={(e) => setCancelPoConfirmPartial(e.target.checked)}
                  data-testid="po-cancel-confirm-partial"
                />
                Yine de iptal et (alınan miktar stoğumda kalır)
              </label>
            )}
            <div>
              <Label>İptal Nedeni (opsiyonel)</Label>
              <Textarea rows={2} value={cancelPoReason} onChange={(e) => setCancelPoReason(e.target.value)} data-testid="po-cancel-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelPoOpen(false)}>Vazgeç</Button>
            <Button
              className="bg-rose-600 hover:bg-rose-500"
              onClick={cancelPo}
              disabled={cancellingPo || (poDetail?.status === "partial_received" && !cancelPoConfirmPartial)}
              data-testid="po-cancel-confirm"
            >
              {cancellingPo && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Siparişi İptal Et
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <BarcodeScanner open={scanOpen} onOpenChange={setScanOpen} onDetected={onScanned} />
      <UnknownBarcodeDialog code={unknownCode} onClose={() => setUnknownCode("")}
        onAssigned={() => { const c = unknownCode; setUnknownCode(""); onScanned(c); }} />
      <Dialog open={!!scanMatches} onOpenChange={(o) => !o && setScanMatches(null)}>
        <DialogContent data-testid="gr-scan-matches">
          <DialogHeader>
            <DialogTitle>Birden fazla açık satır</DialogTitle>
            <DialogDescription>{scanMatches?.label} — doğru sipariş satırını seçin. Adet/stok değişmez.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(scanMatches?.items || []).map(({ po, item, rem }) => (
              <button key={`${po.id}-${item.id}`} onClick={() => focusLine(po.id, item.id)} data-testid={`gr-scan-match-${item.id}`}
                className="w-full text-left rounded border border-border/60 hover:border-emerald-500 p-2 text-sm">
                <div className="font-mono font-semibold">{po.po_no}</div>
                <div className="text-xs text-muted-foreground">{po.supplier_name} · Kalan {rem} {item.unit || ""}</div>
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
